/**
 * CFO Cockpit → Cash. Live bank balances + net position + VAT
 * set-aside + burn/runway, and the 13-week cash-flow forecast where
 * inflows land on each client's REAL expected payment date (median
 * lateness from paid-invoice history), outflows on bill due dates.
 */
import {
  ComposedChart, Bar, Line, XAxis, YAxis, ReferenceLine,
  ResponsiveContainer, Legend, Tooltip as RechartsTooltip,
} from "recharts";
import { Landmark, TrendingDown } from "lucide-react";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { TabState, Card, KpiStrip, InfoTip } from "./cfoShared";
import { useQboData, QboHeader, fmtGbp, fmtDay } from "./qboShared";

const fmtGbpWhole = (v) =>
  v == null || Number.isNaN(v) ? "–" : `£${Math.round(v).toLocaleString("en-GB")}`;

const fmtMonRange = (s, e) => {
  const mon = (iso) => new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, 1)
    .toLocaleDateString("en-GB", { month: "short" });
  return `${mon(s)}–${mon(e)}`;
};

const vatSub = (k) => {
  const v = k.vat;
  if (!v) return k.vat_due_date ? `due ~${fmtDay(k.vat_due_date)}` : "";
  if (v.last_return) {
    const lr = v.last_return;
    let s = `${fmtMonRange(lr.start, lr.end)} return due ~${fmtDay(lr.due)}`;
    if (v.current?.accrued > 0) s += ` · +${fmtGbpWhole(v.current.accrued)} accruing`;
    return s;
  }
  if (v.current) {
    return `accruing ${fmtMonRange(v.current.start, v.current.end)} · due ~${fmtDay(v.current.due)}`;
  }
  return "";
};

export default function CashTab() {
  const { data, loading, error, refreshing, reload } = useQboData("cash");
  if (loading || error) {
    return <TabState loading={loading} error={error} testid="cash-state" />;
  }
  const k = data.kpis || {};
  const chart = (data.weeks || []).map((w) => ({
    ...w,
    label: fmtDay(w.start),
    outflowNeg: -w.outflow,
  }));
  const lowRisk = k.cash_total > 0 && data.low_point?.balance < 0.15 * k.cash_total;

  return (
    <div className="flex flex-col gap-6" data-testid="cash-tab">
      <QboHeader data={data} refreshing={refreshing} onRefresh={reload} testid="cash-refresh-btn" />

      <KpiStrip
        cards={[
          {
            label: "Cash on hand",
            value: fmtGbpWhole(k.cash_total),
            sub: `${(data.banks || []).length} bank account${(data.banks || []).length === 1 ? "" : "s"}`,
            testid: "cash-kpi-cash",
            info: "cash_on_hand",
          },
          {
            label: "Net position",
            value: fmtGbpWhole(k.net_position),
            sub: `+AR ${fmtGbpWhole(k.total_ar)} · −AP ${fmtGbpWhole(k.total_ap)}${k.cc_owed > 0 ? " · −CC" : ""} · −VAT`,
            tone: k.net_position < 0 ? "text-rose-600" : "text-slate-900",
            testid: "cash-kpi-net",
            info: "net_position",
          },
          {
            label: "VAT set-aside",
            value: fmtGbpWhole(k.vat_set_aside),
            sub: vatSub(k),
            testid: "cash-kpi-vat",
            info: "vat_set_aside",
          },
          {
            label: "Runway",
            value: k.runway_months != null ? `${k.runway_months} mo` : "–",
            sub: k.monthly_burn != null ? `burn ${fmtGbpWhole(k.monthly_burn)}/mo` : "burn unavailable",
            tone: k.runway_months != null && k.runway_months < 3 ? "text-rose-600" : "text-slate-900",
            testid: "cash-kpi-runway",
            info: "cash_runway",
          },
        ]}
      />

      {lowRisk && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
          data-testid="cash-low-point-warning"
        >
          <TrendingDown className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Projected cash low point: <strong>{fmtGbp(data.low_point.balance)}</strong> in the
            week of {fmtDay(data.low_point.week)}.
          </span>
        </div>
      )}

      <Card testid="cash-warchest-card">
        <PanelTitle>War chest &amp; working capital <InfoTip id="cash_war_chest" /></PanelTitle>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-3">
          <div data-testid="cash-warchest">
            <ul className="text-xs divide-y divide-stone-50">
              <li className="py-1.5 flex justify-between">
                <span className="text-stone-500">Operating cash (banks)</span>
                <span className="font-mono tabular-nums text-slate-900">{fmtGbpWhole(k.cash_total)}</span>
              </li>
              <li className="py-1.5 flex justify-between">
                <span className="text-stone-500">Investment portfolios</span>
                <span className="font-mono tabular-nums text-slate-900">{fmtGbpWhole(k.investments_portfolio)}</span>
              </li>
              <li className="py-1.5 flex justify-between">
                <span className="text-stone-500">Venture holdings</span>
                <span className="font-mono tabular-nums text-slate-900">{fmtGbpWhole(k.investments_ventures)}</span>
              </li>
              <li className="py-1.5 flex justify-between font-semibold">
                <span className="text-slate-900">Cash &amp; investments</span>
                <span className="font-mono tabular-nums text-[#1B4F8A]" data-testid="cash-warchest-total">
                  {fmtGbpWhole(k.cash_and_investments)}
                </span>
              </li>
            </ul>
            <p className="text-[10px] text-stone-400 mt-2">
              The runway and 13-week forecast deliberately use operating cash only.
            </p>
          </div>
          <div data-testid="cash-wc-days">
            <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mb-2">
              Working capital days · countback <InfoTip id="cash_wc_days" />
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-stone-100 p-3">
                <p className="text-[10px] font-mono text-stone-400">Debtor days (DSO)</p>
                <p className="text-xl font-semibold text-slate-900 font-mono" data-testid="cash-dso">
                  {k.dso_days != null ? `${Math.round(k.dso_days)}d` : "–"}
                </p>
              </div>
              <div className="rounded-xl border border-stone-100 p-3">
                <p className="text-[10px] font-mono text-stone-400">Creditor days (DPO)</p>
                <p className="text-xl font-semibold text-slate-900 font-mono" data-testid="cash-dpo">
                  {k.dpo_days != null ? `${Math.round(k.dpo_days)}d` : "–"}
                </p>
              </div>
              <div className="rounded-xl border border-stone-100 p-3">
                <p className="text-[10px] font-mono text-stone-400">NWC (AR − AP)</p>
                <p className="text-xl font-semibold text-slate-900 font-mono" data-testid="cash-nwc">
                  {fmtGbpWhole((k.total_ar || 0) - (k.total_ap || 0))}
                </p>
              </div>
            </div>
            <p className="text-[10px] text-stone-400 mt-2">
              {k.dso_days != null && k.dpo_days != null
                ? `Clients hold your money ${Math.round(k.dso_days - k.dpo_days)} days longer than you hold your suppliers'.`
                : "Days appear once balances and history load."}
            </p>
          </div>
        </div>
      </Card>

      <Card testid="cash-forecast-card">
        <PanelTitle>
          13-week cash-flow forecast <InfoTip id="cash_forecast" />
        </PanelTitle>
        <p className="text-[10px] text-stone-400 mt-1">{data.behaviour_note}</p>
        <div className="h-64 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={58} />
              <RechartsTooltip
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                formatter={(v, n) => {
                  if (n === "inflow") return [fmtGbp(v), "Expected in"];
                  if (n === "outflowNeg") return [fmtGbp(-v), "Bills out"];
                  return [fmtGbp(v), "Projected balance"];
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 10 }}
                formatter={(v) =>
                  v === "inflow" ? "Expected in" : v === "outflowNeg" ? "Bills out" : "Projected balance"
                }
              />
              <ReferenceLine y={0} stroke="#e7e5e4" />
              <Bar dataKey="inflow" fill="#0D9488" maxBarSize={20} radius={[3, 3, 0, 0]} />
              <Bar dataKey="outflowNeg" fill="#e11d48" maxBarSize={20} radius={[0, 0, 3, 3]} />
              <Line type="monotone" dataKey="balance" stroke="#1B4F8A" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {(data.beyond?.inflow > 0 || data.beyond?.outflow > 0) && (
          <p className="text-[10px] font-mono text-stone-400 mt-2" data-testid="cash-beyond-note">
            Beyond 13 weeks: {fmtGbpWhole(data.beyond.inflow)} expected in ·{" "}
            {fmtGbpWhole(data.beyond.outflow)} bills out
          </p>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="cash-accounts-card">
          <PanelTitle>Bank accounts</PanelTitle>
          <ul className="divide-y divide-stone-50 mt-2">
            {(data.banks || []).map((b) => (
              <li key={b.name} className="py-2 flex items-center gap-3" data-testid={`cash-bank-${b.name}`}>
                <Landmark className="w-3.5 h-3.5 text-stone-300 shrink-0" />
                <span className="text-xs text-slate-800 flex-1 truncate">{b.name}</span>
                {b.currency && b.currency !== "GBP" && (
                  <span className="text-[9px] font-mono text-stone-400">{b.currency}</span>
                )}
                <span className="text-xs font-mono tabular-nums font-semibold text-slate-900">
                  {fmtGbp(b.balance)}
                </span>
              </li>
            ))}
            {(data.cards || []).map((c) => (
              <li key={c.name} className="py-2 flex items-center gap-3">
                <span className="text-xs text-slate-800 flex-1 truncate">{c.name}</span>
                <span className="text-[9px] font-mono text-stone-400">credit card</span>
                <span className={`text-xs font-mono tabular-nums ${c.owed > 0 ? "text-rose-600" : "text-teal-700"}`}>
                  {c.owed > 0 ? `−${fmtGbp(c.owed)}` : fmtGbp(-c.owed)}
                </span>
              </li>
            ))}
            {(data.banks || []).length === 0 && (
              <li className="py-3 text-xs text-stone-400">No bank accounts found in QuickBooks.</li>
            )}
          </ul>
        </Card>

        <Card testid="cash-weeks-card">
          <PanelTitle>Week by week</PanelTitle>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-[11px] border-collapse">
              <thead>
                <tr className="text-[9px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-200">
                  <th className="py-1.5 pr-2 text-left font-medium">Week of</th>
                  <th className="py-1.5 px-2 text-right font-medium">In</th>
                  <th className="py-1.5 px-2 text-right font-medium">Out</th>
                  <th className="py-1.5 pl-2 text-right font-medium">Balance</th>
                </tr>
              </thead>
              <tbody>
                {chart.map((w) => (
                  <tr key={w.start} className="border-b border-stone-50 last:border-0">
                    <td className="py-1 pr-2 font-mono text-stone-500">{w.label}</td>
                    <td className="py-1 px-2 text-right font-mono tabular-nums text-teal-700">
                      {w.inflow > 0 ? fmtGbpWhole(w.inflow) : "–"}
                    </td>
                    <td className="py-1 px-2 text-right font-mono tabular-nums text-rose-600">
                      {w.outflow > 0 ? fmtGbpWhole(w.outflow) : "–"}
                    </td>
                    <td className={`py-1 pl-2 text-right font-mono tabular-nums font-semibold ${w.balance < 0 ? "text-rose-600" : "text-slate-900"}`}>
                      {fmtGbpWhole(w.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
